/**
 * Essential Eight Runtime Page (F100-F103)
 *
 * Shows the Essential Eight as a living control system — NOT a compliance checklist.
 * Features: radar visualization, control cards with maturity/confidence/telemetry.
 * Uses demo data until backend API is available.
 *
 * Route: /essential-eight | Nav: Delivery Governance > Essential Eight
 */
import React from 'react';
import {
  Title2,
  Body1,
  Body2,
  Caption1,
  Card,
  CardHeader,
  Badge,
  tokens,
  makeStyles,
  Divider,
  ProgressBar,
} from '@fluentui/react-components';
import {
  ShieldCheckmarkRegular,
  ArrowTrendingRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  WarningRegular,
  ArrowClockwiseRegular,
} from '@fluentui/react-icons';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { governanceTokens } from '../components/design-system';
import { solutionTokens, tertiaryColors } from '../theme/solution-theme';

// ────── Types ──────────────────────────────────────────

interface E8Control {
  id: string;
  name: string;
  shortName: string;
  maturityLevel: 0 | 1 | 2 | 3;
  targetLevel: 1 | 2 | 3;
  confidence: number; // 0-100
  coverage: number; // 0-100
  assetsTotal: number;
  assetsPassing: number;
  assetsFailing: number;
  trend: 'up' | 'down' | 'stable';
  topFindings: string[];
  lastEvaluation: string;
}

// ────── Demo Data ──────────────────────────────────────

const demoControls: E8Control[] = [
  {
    id: 'e8-1', name: 'Application Control', shortName: 'App Control',
    maturityLevel: 2, targetLevel: 3, confidence: 78, coverage: 72,
    assetsTotal: 48, assetsPassing: 35, assetsFailing: 13, trend: 'up',
    topFindings: ['Unapproved applications detected in dev environments', '3 services missing allow-list configuration'],
    lastEvaluation: '2h ago',
  },
  {
    id: 'e8-2', name: 'Patch Applications', shortName: 'Patch Apps',
    maturityLevel: 2, targetLevel: 3, confidence: 84, coverage: 81,
    assetsTotal: 127, assetsPassing: 103, assetsFailing: 24, trend: 'stable',
    topFindings: ['24 applications with patches older than 48h', 'Critical CVE pending for payment-api'],
    lastEvaluation: '1h ago',
  },
  {
    id: 'e8-3', name: 'Configure Microsoft Office Macros', shortName: 'Office Macros',
    maturityLevel: 3, targetLevel: 3, confidence: 92, coverage: 95,
    assetsTotal: 210, assetsPassing: 200, assetsFailing: 10, trend: 'stable',
    topFindings: ['10 endpoints with macro policy exceptions active'],
    lastEvaluation: '4h ago',
  },
  {
    id: 'e8-4', name: 'User Application Hardening', shortName: 'App Hardening',
    maturityLevel: 1, targetLevel: 2, confidence: 61, coverage: 58,
    assetsTotal: 210, assetsPassing: 122, assetsFailing: 88, trend: 'up',
    topFindings: ['Flash/Java still enabled on 88 endpoints', 'Web browser ad-blocking not enforced'],
    lastEvaluation: '3h ago',
  },
  {
    id: 'e8-5', name: 'Restrict Administrative Privileges', shortName: 'Admin Privileges',
    maturityLevel: 2, targetLevel: 3, confidence: 76, coverage: 70,
    assetsTotal: 85, assetsPassing: 60, assetsFailing: 25, trend: 'down',
    topFindings: ['25 accounts with excessive privileges', 'PAM bypass detected for CI service account'],
    lastEvaluation: '30m ago',
  },
  {
    id: 'e8-6', name: 'Patch Operating Systems', shortName: 'Patch OS',
    maturityLevel: 2, targetLevel: 3, confidence: 80, coverage: 77,
    assetsTotal: 210, assetsPassing: 162, assetsFailing: 48, trend: 'up',
    topFindings: ['48 systems with OS patches older than 14 days', '12 servers running unsupported OS version'],
    lastEvaluation: '2h ago',
  },
  {
    id: 'e8-7', name: 'Multi-Factor Authentication', shortName: 'MFA',
    maturityLevel: 3, targetLevel: 3, confidence: 95, coverage: 97,
    assetsTotal: 340, assetsPassing: 330, assetsFailing: 10, trend: 'stable',
    topFindings: ['10 service accounts without MFA (exception granted)'],
    lastEvaluation: '1h ago',
  },
  {
    id: 'e8-8', name: 'Regular Backups', shortName: 'Backups',
    maturityLevel: 2, targetLevel: 3, confidence: 82, coverage: 85,
    assetsTotal: 64, assetsPassing: 54, assetsFailing: 10, trend: 'up',
    topFindings: ['10 databases without verified backup restoration test', 'Backup retention below 90-day target for analytics-db'],
    lastEvaluation: '6h ago',
  },
];

// ────── Helpers ─────────────────────────────────────────

// #64748b (slate grey, maturity level 0) is not in brand-config.json's palette
// — no token represents it, left as a raw literal.
const maturityColors: Record<number, string> = {
  0: '#64748b',
  1: governanceTokens.colorStatusWarning,
  2: governanceTokens.colorGovernanceSecondary,
  3: governanceTokens.colorStatusSuccess,
};

const trendIcon = (trend: string) => {
  if (trend === 'up') return <ArrowTrendingRegular style={{ color: governanceTokens.colorStatusSuccess, transform: 'rotate(-30deg)' }} />;
  if (trend === 'down') return <ArrowTrendingRegular style={{ color: governanceTokens.colorStatusError, transform: 'rotate(30deg)' }} />;
  return <ArrowClockwiseRegular style={{ color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300 }} />;
};

// ────── Styles ──────────────────────────────────────────

const useStyles = makeStyles({
  headerStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  headerStat: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    textAlign: 'center' as const,
  },
  radarContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXL,
  },
  controlGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  controlCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  controlHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  controlTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  controlMetrics: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: tokens.spacingHorizontalS,
  },
  miniStat: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    textAlign: 'center' as const,
  },
  assetRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  findingsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
});

// ────── Radar SVG Component ─────────────────────────────

const E8Radar: React.FC<{ controls: E8Control[] }> = ({ controls }) => {
  const size = 320;
  const center = size / 2;
  const maxRadius = 130;
  const levels = 3;

  const angleStep = (2 * Math.PI) / 8;
  const startAngle = -Math.PI / 2;

  const getPoint = (index: number, value: number, max: number) => {
    const angle = startAngle + index * angleStep;
    const r = (value / max) * maxRadius;
    return { x: center + r * Math.cos(angle), y: center + r * Math.sin(angle) };
  };

  // Coverage polygon
  const coveragePoints = controls.map((c, i) => getPoint(i, c.coverage, 100));
  const coveragePath = coveragePoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';

  // Confidence polygon
  const confidencePoints = controls.map((c, i) => getPoint(i, c.confidence, 100));
  const confidencePath = confidencePoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Grid circles — #334155/#94a3b8 are a fixed dark-radar-chart slate scale
          (not brand colours, no exact Fluent neutral-token match); left as raw
          literals to avoid shifting this chart's rendered appearance. */}
      {Array.from({ length: levels }, (_, i) => {
        const r = ((i + 1) / levels) * maxRadius;
        return <circle key={i} cx={center} cy={center} r={r} fill="none" stroke="#334155" strokeWidth="0.5" />;
      })}

      {/* Axis lines and labels */}
      {controls.map((c, i) => {
        const end = getPoint(i, 100, 100);
        const labelPos = getPoint(i, 115, 100);
        return (
          <g key={c.id}>
            <line x1={center} y1={center} x2={end.x} y2={end.y} stroke="#334155" strokeWidth="0.5" />
            <text
              x={labelPos.x}
              y={labelPos.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#94a3b8"
              fontSize="9"
              fontFamily="inherit"
            >
              {c.shortName}
            </text>
          </g>
        );
      })}

      {/* Coverage area */}
      <path d={coveragePath} fill={governanceTokens.colorGovernancePrimary} fillOpacity="0.15" stroke={governanceTokens.colorGovernancePrimary} strokeWidth="2" />

      {/* Confidence area */}
      <path d={confidencePath} fill={solutionTokens.brandSecondary} fillOpacity="0.1" stroke={solutionTokens.brandSecondary} strokeWidth="1.5" strokeDasharray="4 2" />

      {/* Data points */}
      {controls.map((c, i) => {
        const p = getPoint(i, c.coverage, 100);
        return (
          <circle key={c.id} cx={p.x} cy={p.y} r="4" fill={maturityColors[c.maturityLevel]} stroke={tertiaryColors?.hex} strokeWidth="1.5" />
        );
      })}
    </svg>
  );
};

// ────── Main Page ───────────────────────────────────────

export const EssentialEightPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const avgCoverage = Math.round(demoControls.reduce((s, c) => s + c.coverage, 0) / 8);
  const avgConfidence = Math.round(demoControls.reduce((s, c) => s + c.confidence, 0) / 8);
  const avgMaturity = (demoControls.reduce((s, c) => s + c.maturityLevel, 0) / 8).toFixed(1);
  const totalFailing = demoControls.reduce((s, c) => s + c.assetsFailing, 0);
  const controlsMeetingTarget = demoControls.filter(c => c.maturityLevel >= c.targetLevel).length;
  const controlsBelowTarget = demoControls.filter(c => c.maturityLevel < c.targetLevel).length;
  const highestRisk = [...demoControls].sort((a, b) => a.coverage - b.coverage)[0];

  return (
    <div className={ps.page}>
      <PageHeader
        title="Essential Eight Runtime"
        subtitle="Living control telemetry across the ACSC Essential Eight mitigation strategies"
        icon={<ShieldCheckmarkRegular />}
        iconColor={governanceTokens.colorGovernancePrimary}
      />

      {/* Header Strip */}
      <div className={styles.headerStrip}>
        <div className={styles.headerStat}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Average Coverage</Caption1>
          <Title2 style={{ color: governanceTokens.colorGovernancePrimary }}>{avgCoverage}%</Title2>
        </div>
        <div className={styles.headerStat}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Average Confidence</Caption1>
          <Title2 style={{ color: solutionTokens.brandSecondary }}>{avgConfidence}%</Title2>
        </div>
        <div className={styles.headerStat}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Mean Maturity</Caption1>
          <Title2>{avgMaturity} / 3</Title2>
        </div>
        <div className={styles.headerStat}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Assets Out of Compliance</Caption1>
          <Title2 style={{ color: totalFailing > 0 ? governanceTokens.colorStatusWarning : governanceTokens.colorStatusSuccess }}>
            {totalFailing}
          </Title2>
        </div>
      </div>

      {/* Governance Summary + Highest Risk Control */}
      <div className={ps.twoColumn}>
        <Card>
          <CardHeader header={<Title2>Essential Eight Governance Summary</Title2>} />
          <div className={ps.cardBody}>
            <div className={ps.rowList}>
              <div className={ps.rowItem}>
                <Body1>Average Maturity</Body1>
                <Body2 style={{ fontWeight: 600 }}>{avgMaturity} / 3</Body2>
              </div>
              <div className={ps.rowItem}>
                <Body1>Controls Meeting Target</Body1>
                <Badge appearance="filled" color="success">{controlsMeetingTarget} of 8</Badge>
              </div>
              <div className={ps.rowItem}>
                <Body1>Controls Below Target</Body1>
                <Badge appearance="filled" color={controlsBelowTarget > 0 ? 'danger' : 'success'}>{controlsBelowTarget} of 8</Badge>
              </div>
              <div className={ps.rowItem}>
                <Body1>Assets Out of Compliance</Body1>
                <Body2 style={{ fontWeight: 600, color: totalFailing > 0 ? governanceTokens.colorStatusWarning : undefined }}>{totalFailing}</Body2>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader
            header={<Title2>Highest Risk Control</Title2>}
            description="Control with lowest coverage requiring immediate attention"
          />
          <div className={ps.cardBody}>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
              <WarningRegular style={{ fontSize: tokens.fontSizeHero800, color: governanceTokens.colorStatusError }} />
              <div style={{ flex: 1 }}>
                <Body1 style={{ fontWeight: 600 }}>{highestRisk.name}</Body1>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                  Coverage: {highestRisk.coverage}% | Maturity: ML{highestRisk.maturityLevel} (target: ML{highestRisk.targetLevel})
                </Body2>
              </div>
              <Badge appearance="filled" color="danger" size="large">
                {highestRisk.assetsFailing} failing
              </Badge>
            </div>
            {highestRisk.topFindings.length > 0 && (
              <div style={{ marginTop: tokens.spacingVerticalS }}>
                {highestRisk.topFindings.map((f, i) => (
                  <Body2 key={i} style={{ color: tokens.colorNeutralForeground3, display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'flex-start' }}>
                    {/* 3px: no token between spacingVerticalXXS (2px) and spacingVerticalXS (4px) — left as-is. */}
                    <WarningRegular style={{ fontSize: tokens.fontSizeBase200, flexShrink: 0, marginTop: '3px', color: governanceTokens.colorStatusWarning }} />
                    {f}
                  </Body2>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Radar */}
      <Card>
        <CardHeader
          header={<Title2>Control Radar</Title2>}
          description="Coverage (solid) and confidence (dashed) across all eight controls"
        />
        <div className={styles.radarContainer}>
          <E8Radar controls={demoControls} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: tokens.spacingHorizontalXL, paddingBottom: tokens.spacingVerticalL }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
            <div style={{ width: 16, height: 3, backgroundColor: governanceTokens.colorGovernancePrimary, borderRadius: 2 }} />
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Coverage</Caption1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
            <div style={{ width: 16, height: 3, backgroundColor: solutionTokens.brandSecondary, borderRadius: 2, borderTop: `1px dashed ${solutionTokens.brandSecondary}` }} />
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Confidence</Caption1>
          </div>
        </div>
      </Card>

      {/* Control Cards */}
      <Card>
        <CardHeader header={<Title2>Control Telemetry</Title2>} />
        <div className={ps.cardBody}>
          <div className={styles.controlGrid}>
            {demoControls.map((control) => (
              <div key={control.id} className={styles.controlCard}>
                {/* Header */}
                <div className={styles.controlHeader}>
                  <div className={styles.controlTitle}>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      backgroundColor: maturityColors[control.maturityLevel],
                    }} />
                    <Body1 style={{ fontWeight: 600 }}>{control.name}</Body1>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}>
                    {trendIcon(control.trend)}
                    <Badge
                      appearance="tint"
                      color={control.coverage >= 80 ? 'success' : control.coverage >= 50 ? 'warning' : 'danger'}
                      size="small"
                    >
                      {control.coverage >= 80 ? 'Operational' : control.coverage >= 50 ? 'Degraded' : 'Failing'}
                    </Badge>
                    <Badge
                      appearance="filled"
                      color={control.maturityLevel >= control.targetLevel ? 'success' : control.maturityLevel >= 2 ? 'warning' : 'danger'}
                      size="small"
                    >
                      ML{control.maturityLevel}
                    </Badge>
                  </div>
                </div>

                {/* Metrics */}
                <div className={styles.controlMetrics}>
                  <div className={styles.miniStat}>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Coverage</Caption1>
                    <Body2 style={{ fontWeight: 600, color: governanceTokens.colorGovernancePrimary }}>{control.coverage}%</Body2>
                  </div>
                  <div className={styles.miniStat}>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Confidence</Caption1>
                    <Body2 style={{ fontWeight: 600, color: solutionTokens.brandSecondary }}>{control.confidence}%</Body2>
                  </div>
                  <div className={styles.miniStat}>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Target</Caption1>
                    <Body2 style={{ fontWeight: 600 }}>ML{control.targetLevel}</Body2>
                  </div>
                </div>

                {/* Coverage Bar */}
                <ProgressBar
                  value={control.coverage / 100}
                  thickness="large"
                  color={control.coverage >= 80 ? 'success' : control.coverage >= 60 ? 'warning' : 'error'}
                />

                {/* Asset Pass/Fail */}
                <div className={styles.assetRow}>
                  <CheckmarkCircleRegular style={{ color: governanceTokens.colorStatusSuccess, fontSize: tokens.fontSizeBase300 }} />
                  <Caption1>{control.assetsPassing} passing</Caption1>
                  <DismissCircleRegular style={{ color: governanceTokens.colorStatusError, fontSize: tokens.fontSizeBase300, marginLeft: tokens.spacingHorizontalM }} />
                  <Caption1>{control.assetsFailing} failing</Caption1>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, marginLeft: 'auto' }}>
                    {control.lastEvaluation}
                  </Caption1>
                </div>

                {/* Top Findings */}
                {control.topFindings.length > 0 && (
                  <>
                    <Divider style={{ opacity: 0.3 }} />
                    <div className={styles.findingsList}>
                      {control.topFindings.map((f, i) => (
                        <div key={i} style={{ display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'flex-start' }}>
                          <WarningRegular style={{ color: governanceTokens.colorStatusWarning, fontSize: tokens.fontSizeBase200, flexShrink: 0, marginTop: tokens.spacingVerticalXXS }} />
                          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{f}</Caption1>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};
